// mediapipeLoader — lazy CDN loader for @mediapipe/tasks-vision.
//
// MediaPipe is heavy (~2-5MB JS + WASM + per-model .task files). We do NOT
// bundle it — the test page loads it from jsdelivr's CDN on demand. This:
//   * Keeps the initial JS bundle small (faster cold-load for candidates)
//   * Avoids needing `npm install @mediapipe/tasks-vision` in the build
//   * Leverages jsdelivr's heavy CDN caching across our entire user base
//
// The loader caches the loaded modules in a module-level promise so multiple
// detectors instantiating concurrently only trigger one fetch.
//
// Failure handling: if the CDN is unreachable (corporate firewall, slow
// network), `loadVisionTasks()` rejects with a clear error. Callers should
// catch and gracefully degrade — proctoring engine will skip MediaPipe-only
// detectors but keep browser-event detectors running.

const VISION_VERSION = '0.10.14';
const VISION_ESM_URL =
  `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${VISION_VERSION}`;
const WASM_BASE =
  `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${VISION_VERSION}/wasm`;

// Audio bundle ships separately from vision so we only load it when an
// audio sub-feature (e.g. `mediapipe.audio_classifier`) is enabled. Same
// CDN pattern, separate WASM directory.
const AUDIO_VERSION = '0.10.14';
const AUDIO_ESM_URL =
  `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-audio@${AUDIO_VERSION}`;
const AUDIO_WASM_BASE =
  `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-audio@${AUDIO_VERSION}/wasm`;

// Model URLs — Google-hosted, very stable.
const MODELS = Object.freeze({
  faceLandmarker:
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
  handLandmarker:
    'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
  // Audio classifier (YAMNet) for Layer 3 audio sub-feature (next batch).
  audioClassifier:
    'https://storage.googleapis.com/mediapipe-models/audio_classifier/yamnet/float32/1/yamnet.tflite',
});

let _visionPromise = null;
let _audioPromise = null;

/**
 * Load and return the @mediapipe/tasks-vision ESM module + a ready
 * FilesetResolver. Cached across the whole app — call this from every
 * detector; it only fetches once.
 *
 * @returns {Promise<{ vision: object, FilesetResolver: object, modules: object }>}
 */
export function loadVisionTasks() {
  if (_visionPromise) return _visionPromise;

  _visionPromise = (async () => {
    // Dynamic ESM import directly from CDN. Webpack will leave this alone
    // because the URL is a string literal at runtime (not statically
    // analysable as an import). Use the /* webpackIgnore */ hint so the
    // bundler definitely doesn't try to inline this — we WANT runtime
    // fetch behaviour.
    const modules = await import(
      /* webpackIgnore: true */ `${VISION_ESM_URL}/vision_bundle.mjs`
    );

    const { FilesetResolver } = modules;
    const vision = await FilesetResolver.forVisionTasks(WASM_BASE);

    return { vision, FilesetResolver, modules };
  })().catch((err) => {
    // Drop the cache so a retry isn't permanently poisoned.
    _visionPromise = null;
    throw err;
  });

  return _visionPromise;
}

/**
 * Same pattern as loadVisionTasks() but for the audio bundle. Separate
 * cached promise so a vision-only test never downloads audio code.
 *
 * @returns {Promise<{ audio: object, FilesetResolver: object, modules: object }>}
 */
export function loadAudioTasks() {
  if (_audioPromise) return _audioPromise;

  _audioPromise = (async () => {
    const modules = await import(
      /* webpackIgnore: true */ `${AUDIO_ESM_URL}/audio_bundle.mjs`
    );
    const { FilesetResolver } = modules;
    const audio = await FilesetResolver.forAudioTasks(AUDIO_WASM_BASE);
    return { audio, FilesetResolver, modules };
  })().catch((err) => {
    _audioPromise = null;
    throw err;
  });

  return _audioPromise;
}


/** Expose model URLs for detectors. */
export const MODEL_URLS = MODELS;

/**
 * Helper: detect whether the runtime can actually load MediaPipe. Useful
 * for the ProctoringEngine to log a one-time warning if the loader fails
 * (e.g. corporate firewall blocks jsdelivr) so we know why MediaPipe
 * detectors silently degraded.
 */
export async function checkMediaPipeAvailable() {
  try {
    await loadVisionTasks();
    return { available: true };
  } catch (err) {
    return {
      available: false,
      error: err?.message || String(err),
    };
  }
}
